import { SapConfig } from './sap.config';
import { Injectable, EventEmitter, inject } from '@angular/core';
import { throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, IAppConfig } from '../../shared/config/app.config';
import { NGXLogger } from 'ngx-logger';

/**
 * Office service for all the office related api call
 */
@Injectable()
export class ConfigService {
  /**
   * Returns the TPA USAP value based on the current phase (Angular 6 logic)
   */
  public getTpaUsap(): string {
    let usap = '';
    let phase = this.guessPhaseFromLocation();
    usap = SapConfig.usap['DEFAULT_TPA'] + SapConfig.getPhaseLetter(phase);
    return usap;
  }

  /**
   * Guess the current phase from the location (Angular 6 logic)
   */
  private guessPhaseFromLocation(): string {
    let phase = 'usr';  // default phase
    let host = location.host;
    if (host.startsWith('www.')) {
      host = host.substring(4);
    }
    if (host.endsWith('amadeus.com')) {
      const chunks = host.split('.');
      if (chunks.length > 0) {
        phase = (chunks[0] === 'office-management') ? 'prd' : chunks[0];
      }
    }
    if (host.startsWith('ngeum')) {
      phase = 'USR';
    }
    return phase;
  }
  /**
   * Returns the current arpUsap value (for ARP endpoint logic)
   */
  public getOffUsap(): string {
    return this.arpUsap || '';
  }
  // LOGGING: Trace config and env usage

  public isLoggedIn$: EventEmitter<boolean>;
  public arpUsap: string; // Added for ARP support (restored from Angular 6)
  public arpOrigin: string; // Added for ARP support (restored from Angular 6)
  private appConfig: IAppConfig;
  private config: any;
  private env: any;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger
  ) {
    this.appConfig = inject(APP_CONFIG) as IAppConfig;
    // constructor
    this.isLoggedIn$ = new EventEmitter();
  }

  public setConfigData(key: string, value: any) {
    this.logger.debug('[ConfigService] setConfigData', key, value);
    this.appConfig[key] = value;
  }

  public getConfigData(key: string): any {
    this.logger.debug('[ConfigService] getConfigData', key, this.appConfig[key]);
    if (this.appConfig && this.appConfig[key]) {
      return this.appConfig[key];
    }
    return null;
  }
  /**
   * Use to get the data found in the second file (config file)
   */
  public getConfig(key: any) {
    this.logger.debug('[ConfigService] getConfig', key, this.config ? this.config[key] : undefined);
    if (this.config && this.config[key]) {
      return this.config[key];
    }
  }

  /**
   * Use to get the data found in the first file (env file)
   */
  public getEnv() {
    this.logger.debug('[ConfigService] getEnv', this.env);
    return this.env;
  }

  public isLoggedIn(value: boolean) {
    this.isLoggedIn$.emit(value);
  }

  /**
   * This method:
   *   a) Get env from token
   *   b) Loads "config.json" to get all env's variables
   *   (e.g.: 'config.json')
   */
  public load() {
    this.logger.info('[ConfigService] load() called');
    return new Promise((resolve, reject) => {
      let envResponse: any = null;
      if (!envResponse) {
        if (this.appConfig.env[location.hostname]) {
          envResponse = this.appConfig.env[location.hostname];
        }
      }
      envResponse = envResponse ? envResponse.toLowerCase() : 'dev';
      this.env = envResponse;
      let request: any = this.http.get('assets/config/config.json');
      this.logger.info('[ConfigService] Starting config.json request...');
      if (request) {
        request
          .pipe(
            map((res: any) => res), // HttpClient already parses JSON
            catchError((error: any) => {
              this.logger.error('[ConfigService] Error reading configuration file', error);
              resolve(error);
              let retError = null;
              try {
                if (error && error.error) {
                  retError = error.error;
                }
              } catch (e) { }
              return throwError(() => retError || 'Server error');
            })
          )
          .subscribe((responseData) => {
            this.logger.info('[ConfigService] config.json response:', responseData);
            if (responseData && responseData.data) {
              this.setConfig(responseData.data);
            } else {
              this.logger.error('[ConfigService] config.json missing "data" property:', responseData);
            }
            resolve(true);
          });
      } else {
        this.logger.error('[ConfigService] Env config (' + envResponse + ') is not valid');
        resolve(true);
      }
    });
  }

  private setConfig(value) {
    this.logger.debug('[ConfigService] setConfig', value);
    this.config = value;
    if (!this.config) {
      this.logger.error('[ConfigService] setConfig: config is undefined after assignment!');
    }
  }
}
